import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { usePermissions, ROUTE_PERMISSION_MAP } from '../../context/PermissionsContext';
import { AccessDenied } from '../ui/AccessDenied';
import { verifySession } from '../../utils/verifySession';

const OUTAGE_STATUSES = new Set([502, 503, 504]);
// No answer from the server (axios sets request; fetch-style errors say "Network Error").
const isOutage = (error) => (error?.response
    ? OUTAGE_STATUSES.has(error.response.status)
    : !!(error?.request || error?.code === 'ERR_NETWORK' || error?.code === 'ECONNABORTED' || /network error/i.test(error?.message || '')));

const ProtectedRoute = () => {
    const { name,formId } = useParams();
    const [authStatus, setAuthStatus] = useState(null);
    const navigate=useNavigate();
    const location = useLocation();
    const { permissionsLoaded, hasPageAccess } = usePermissions();

    const isReferralUser = (() => {
        try {
            const u = JSON.parse(localStorage.getItem('cognitoUser') || '{}');
            return u.role === 'referral';
        } catch { return false; }
    })();

    useEffect(() => {
        const verifyToken = async () => {
            const token = localStorage.getItem("sessionToken");
            if (!token) {
                setAuthStatus(false);
                return;
            }

            try {
                // Shared with PermissionsContext, which reads the embedded permissions.
                const response = await verifySession(token, localStorage.getItem("cognitoIdToken"));
                if (response.status === 200) {
                    setAuthStatus(true);
                } else {
                    setAuthStatus(false);
                }
            } catch (error) {
                // The sign-in service being down (502/503/504) or no connection keeps the
                // session: the page loads, and its own requests report what fails. Any
                // other failure (a refused login, an error answer) goes to the login page.
                setAuthStatus(!error.sessionEnded && isOutage(error));
            }
        };

        verifyToken();
    }, [navigate]);


    if (authStatus === null) {
        return <div>Loading...</div>;
    }

    if ((name && !formId) || (!name && formId)) {
        return <Navigate to="/pagenotfound" />;
    }

    if(name && formId){
        return <Outlet />
    }
    if (!authStatus) {
        return <Navigate to="/login" />;
    }
    if (!permissionsLoaded) {
        return <div>Loading...</div>;
    }

    const pathname = location.pathname;
    const basePath = '/' + (pathname.split('/')[1] || '');
    const permKey = ROUTE_PERMISSION_MAP[pathname] || ROUTE_PERMISSION_MAP[basePath];

    if (permKey && !hasPageAccess(permKey)) {
        return <AccessDenied variant="page" />;
    }

    return <Outlet />;
};

export default ProtectedRoute;
